import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getUIErrorMessage } from '@/utils/errors';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { poFileUploadSchema, UploadProcessObjectFileFormSchema } from './schemas';
import { MeasurementGroupLoaderResponse } from '../types';

import { Button } from '@/components/ui/button';

import { uploadProcessObjectFile } from './constants';
import { toast } from 'sonner';
import ErrorAlert from '@/components/error-alert/ErrorAlert';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { cn } from '@/lib/utils';
import Spinner from '@/components/spinner/Spinner';
import { useLoaderData } from 'react-router';

type Props = {
	title?: string;
	isOpen: boolean;
	onClose: () => void;
};

const ProcessObjectFileUpload = ({ title, isOpen, onClose }: Props) => {
	const [showConfirmation, setShowConfirmation] = useState(false);
	const {
		measurementGroup: { data: measurementGroupData }
	} = useLoaderData() as MeasurementGroupLoaderResponse;

	const { isDesktop } = useIsDesktopSize();

	const poFileUploadForm = useForm<UploadProcessObjectFileFormSchema>({
		resolver: zodResolver(poFileUploadSchema),
		defaultValues: {
			processObjectId: '',
			description: '',
			files: undefined
		}
	});

	const [error, setError] = useState('');

	const { mutate: uploadFile, isPending } = useMutation({
		mutationFn: uploadProcessObjectFile,
		mutationKey: ['measurement-groups', measurementGroupData.id, 'files'],
		onSuccess: async () => {
			onClose();
			poFileUploadForm.reset();
			toast('The files have been uploaded successfully.');
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleCloseForm = () => {
		poFileUploadForm.reset();
		onClose();
		setShowConfirmation(false);
		setError('');
	};

	const processObjectIds = measurementGroupData.devices.map(device => device.poId.toString());

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn('h-full p-10 overflow-auto', { 'sm:max-w-xl ': isDesktop, 'w-full h-5/6': !isDesktop })}
			>
				<SheetHeader>
					{title != null && title !== '' && <SheetTitle>{title?.trim() || 'Upload process object files'}</SheetTitle>}
					<SheetDescription>Fill in the file details. Click save when you're done.</SheetDescription>
				</SheetHeader>
				<Form {...poFileUploadForm}>
					<form className="max-w-xl flex flex-col gap-5 mt-10">
						<FormField
							control={poFileUploadForm.control}
							name="processObjectId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Process Object</FormLabel>

									<Select onValueChange={field.onChange} value={field.value}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="Select a process object" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{processObjectIds.map(id => (
												<SelectItem key={id} value={id}>
													{id}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							name="description"
							control={poFileUploadForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Description</FormLabel>
									<FormControl>
										<Input
											type="text"
											placeholder="File description here"
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							name="files"
							control={poFileUploadForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Files</FormLabel>
									<FormControl>
										<Input
											type="file"
											multiple
											className="cursor-pointer leading-7"
											onChange={e => field.onChange(Array.from(e.target.files ?? []))}
											onBlur={field.onBlur}
											name={field.name}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</form>
				</Form>

				{error && <ErrorAlert title="Error" description={error}></ErrorAlert>}

				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Submit
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>
								<span className="mr-2">
									You will upload {poFileUploadForm.watch('files')?.length ?? 0} file(s) for the process
									object with ID:
								</span>
								<b>{poFileUploadForm.watch('processObjectId')}</b>
							</p>

							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isPending}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={() => {
										poFileUploadForm.handleSubmit(val => uploadFile(val))();
									}}
									disabled={isPending}
								>
									{error ? 'Try again ' : 'Save'}
									{isPending && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};

export default ProcessObjectFileUpload;
